/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddTestCaseArtifact.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomAutoCompleteWithAPIV2 from "../../../components/customComponentsV2/common/searchBar/CustomAutoCompleteWithAPIV2";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../config/appTheme";
import ViewFilesDialog from "../../test-cases/components/ViewFilesDialog";
import ViewImage from "./ViewImage";
import testCasesActions from "../../../redux/actions/testCasesActions";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import { HandleApiActions } from "../../../redux/actions";
function AddTestCaseArtifact({
  selectedTicketsTableRowData = [],
  setSelectedTicketsTableRowData = [],
  onClose,
  fetchTickets,
}) {
  const dispatch = useDispatch();
  const profileDetails = useSelector((store) => store.profileReducer);

  // Test cases
  const [testCaseOptionsList, setTestCaseOptionsList] = useState([]);
  const [selectedTestCase, setSelectedTestCase] = useState(null);

  // Run test case data
  const [selectedRunTestCaseIds, setSelectedRunTestCaseIds] = useState([]);
  const [isRunAllTestCasesSelected, setIsRunAllTestCasesSelected] =
    useState(false);
  const [savedRunTestCases, setSavedRunTestCases] = useState([]);

  const [openViewFilesDialog, setOpenViewFilesDialog] = useState(false);
  const [selectedTestCaseArtifactData, setSelectedTestCaseArtifactData] =
    useState(null);

  const [filteredTestCase, setFilteredTestCase] = useState([]);
  const [isAddArtifactsScreenOpen, setIsAddArtifactsScreenOpen] =
    useState(true);

  const [reviewTableRowsDataArtifact, setReviewTableRowsDataArtifact] =
    useState([]);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewRowsPerPage, setReviewRowsPerPage] = useState(25);
  const [reviewTotalCount, setReviewTotalCount] = useState(0);

  const clearDialogFields = () => {
    setIsAddArtifactsScreenOpen(true);
    setTestCaseOptionsList([]);
    setSelectedTestCase(null);

    setSelectedTestCaseArtifactData(null);
    setIsRunAllTestCasesSelected(false);
    setSelectedRunTestCaseIds([]);
    setSavedRunTestCases([]);

    setSelectedTicketsTableRowData(null);
  };

  const getEntityIdBasedOnType = (data, entityT) => {
    switch (selectedTicketsTableRowData?.ticket_type) {
      case 1:
        return {
          entity_id: selectedTicketsTableRowData?.swm_details?.swm_id,
          entity_type: "swm",
        };
      case 2:
        return {
          entity_id: selectedTicketsTableRowData?.ee_details?.ee_id,
          entity_type: "ee",
        };
      case 3:
        return {
          entity_id: selectedTicketsTableRowData?.vehicle_component_details?.id,
          entity_type: "vc",
        };
      case 6:
        return {
          entity_id: selectedTicketsTableRowData?.vehicle_system_details?.id,
          entity_type: "vs",
        };
      default:
        return null;
    }
  };
  // Handle get the run test cases
  const handleGetRunTestCases = async ({ testCaseId }) => {
    let { entity_id, entity_type } = getEntityIdBasedOnType();

    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_campaign_result_test_cases,
        params: {
          test_case_id: testCaseId,
          page_number: page,
          page_size: rowsPerPage,
          submission_type: "active",
          entity_id: entity_id,
          entity_type: entity_type,
          project_id: selectedTicketsTableRowData?.project_details?.project_id,
        },
      }),
    );
    if (resp.error) {
      setFilteredTestCase([]);
      setSelectedRunTestCaseIds([]);
      setIsRunAllTestCasesSelected(false);
    } else {
      let linkedTestCase =
        selectedTicketsTableRowData?.artefact_details
          ?.filter((data) => data.artefact_type == "RUN")
          ?.map((e) => e.run_id) || [];
      const filteredTableRowsData = resp?.data?.results
        ?.filter((e) => !linkedTestCase.includes(e.run_id))
        .map((tc, index) => {
          return {
            ...tc,
            id: (page - 1) * rowsPerPage + index + 1,
            executed_at_tz: convertUtcToTimeZone(
              tc?.executed_at,
              profileDetails?.profile?.profile?.region,
            ),
          };
        });
      setFilteredTestCase(filteredTableRowsData);
      // Auto-select previously saved run IDs if available
      const matchedTestCase = savedRunTestCases?.find(
        (item) => item?.test_case_id == selectedTestCase?.value,
      );
      const previouslySelectedRunIds = matchedTestCase?.run_ids ?? [];
      setSelectedRunTestCaseIds(previouslySelectedRunIds);
      setIsRunAllTestCasesSelected(false);
    }
  };

  const handleGetReviewTestCaseArtifacts = async ({ savedRunTestCases }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_test_case_artifacts,
        params: {
          selected_artifacts: savedRunTestCases,
          page_number: reviewPage,
          page_size: reviewRowsPerPage,
        },
      }),
    );
    if (resp.error) {
      setReviewTableRowsDataArtifact([]);
    } else {
      const filteredTableRowsDataArtifact = resp?.data?.map((tc, index) => {
        return {
          ...tc,
          id: (reviewPage - 1) * reviewRowsPerPage + index + 1,
          executed_at_tz: convertUtcToTimeZone(
            tc?.executed_at,
            profileDetails?.profile?.profile?.region,
          ),
        };
      });
      setReviewTableRowsDataArtifact(filteredTableRowsDataArtifact);
    }
  };

  // Set test cases option list
  useEffect(() => {
    const filteredTestCaseOptionsList =
      selectedTicketsTableRowData?.test_case_details?.map((tc) => ({
        value: tc?.test_case_id,
        label: tc?.manual_test_case_id,
      }));

    setTestCaseOptionsList(filteredTestCaseOptionsList);
  }, [selectedTicketsTableRowData]);

  // Select or unselect all the run test cases
  useEffect(() => {
    if (isRunAllTestCasesSelected) {
      const allRunIds = filteredTestCase?.map((row) => row?.run_id);
      setSelectedRunTestCaseIds(allRunIds);
    } else {
      // Only clear selections if user explicitly unchecked "select all"
      // Don't clear when isRunAllTestCasesSelected becomes false due to individual deselection
      const allRunIds = filteredTestCase?.map((row) => row?.run_id) ?? [];
      const isCurrentlyAllSelected =
        allRunIds.length > 0 &&
        allRunIds.every((id) => selectedRunTestCaseIds.includes(id));
      if (isCurrentlyAllSelected) {
        setSelectedRunTestCaseIds([]);
      }
    }
  }, [isRunAllTestCasesSelected]);

  // Keep isRunAllTestCasesSelected in sync with selectedRunTestCaseIds
  useEffect(() => {
    const allRunIds = filteredTestCase?.map((row) => row?.run_id) ?? [];
    const isAllSelected =
      allRunIds.length > 0 &&
      allRunIds.every((id) => selectedRunTestCaseIds.includes(id));
    setIsRunAllTestCasesSelected(isAllSelected);
  }, [selectedRunTestCaseIds]);
  const handleCreateTestCaseArtifacts = async ({
    intrId,
    selectedTestCaseArtifactIds,
    tableRowsDataArtifact,
  }) => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.create_test_case_artifacts,
        params: {
          intr_id: intrId,
          selected_artefacts: tableRowsDataArtifact
            ?.filter(
              (artifact) =>
                artifact?.run_id &&
                selectedTestCaseArtifactIds.includes(artifact.run_id),
            )
            ?.map((artifact) => ({
              test_case_id: artifact?.test_case_id ?? null,
              test_case_name: artifact?.test_case_name ?? "",
              manual_test_case_id: artifact?.manual_test_case_id ?? "",
              run_id: artifact?.run_id ?? null,
              result: artifact?.result ?? "",
              executed_at: artifact?.executed_at ?? null,
              artefact_link: Array.isArray(artifact?.artefact_link)
                ? artifact.artefact_link
                : [],
            })),
        },
      }),
    );
    if (!resp.error) {
      onClose();
      clearDialogFields();
      fetchTickets();
    }
  };
  const COLUMNS_ARTIFACT = [
    {
      field: "Select",
      headerName: (
        <CustomCheckBoxV2
          // label={"Select All"}
          value={isRunAllTestCasesSelected}
          onChange={(e) => setIsRunAllTestCasesSelected(e.target.checked)}
        />
      ),
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params) => (
        <CustomCheckBoxV2
          // value={selectedRunTestCaseIds.includes(params?.row?.run_id)}
          // onChange={() => {
          //   const currentId = params?.row?.run_id;
          //   setSelectedRunTestCaseIds((prev) =>
          //     prev.includes(currentId)
          //       ? prev.filter((id) => id !== currentId)
          //       : [...prev, currentId],
          //   );
          // }}
          value={isRunTestCaseSelected(params?.row?.run_id)}
          onChange={() => handleRunTestCaseToggle(params?.row?.run_id)}
        />
      ),
    },
    {
      field: "id",
      headerName: "S.No",
      minWidth: 60,
      maxWidth: 80,
      filterable: false,
      sortable: false,
    },
    {
      field: "manual_test_case_id",
      headerName: "Test Case ID",
      flex: 1,
      minWidth: 120,
    },
    { field: "run_id", headerName: "Run Id", flex: 1, minWidth: 120 },
    {
      field: "result",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          sx={{
            ...componentStyle.font.tableText,
          }}
        >
          {params.row.result}
        </Typography>
      ),
    },
    {
      field: "artefact_link",
      headerName: "Attachment",
      width: 150,
      renderCell: (params) => {
        return (
          <CustomButtonV2
            type={"blue-outline"}
            disabled={!params?.row?.artefact_link?.length}
            btnName={"View"}
            variant={"text"}
            handleOnClick={() => {
              setSelectedTestCaseArtifactData({ ...params?.row });
              setOpenViewFilesDialog(true);
            }}
          />
        );
      },
    },
    {
      field: "executed_at_tz",
      headerName: "Executed At",
      flex: 1,
      minWidth: 120,
    },
  ];

  // sonar handler functions
  const handleRunTestCaseToggle = (currentId) => {
    setSelectedRunTestCaseIds((prev) =>
      prev.includes(currentId)
        ? prev.filter((id) => id !== currentId)
        : [...prev, currentId],
    );
  };

  const isRunTestCaseSelected = (id) => selectedRunTestCaseIds.includes(id);

  return (
    <Grid container>
      <Grid
        item
        sm={12}
        sx={{ display: "flex", justifyContent: "center", gap: 2 }}
      >
        <Box sx={{ width: "200px" }}>
          <CustomSelectV2
            optionsList={testCaseOptionsList ?? []}
            value={selectedTestCase}
            handleOnChange={(value) => {
              setSelectedTestCase(value.target.value);
            }}
            showLabel={false}
            label="Select Test Case"
          />
        </Box>
        {isAddArtifactsScreenOpen && (
          <Box>
            <CustomButtonV2
              btnName={"Pull"}
              disabled={!selectedTestCase}
              handleOnClick={() => {
                handleGetRunTestCases({
                  testCaseId: selectedTestCase,
                });
              }}
            />
          </Box>
        )}
      </Grid>
      {isAddArtifactsScreenOpen ? (
        <Box>
          <Box>
            <Typography
              sx={{ ...componentStyle.font.cardHeadder, fontWeight: 500 }}
            >
              Test Case Name:{" "}
              {filteredTestCase?.length > 0
                ? filteredTestCase[0]?.test_case_name || "-"
                : "-"}
            </Typography>
          </Box>
          <Grid item sm={12}>
            <Box sx={{ height: "250px" }}>
              <CustomDataGridV2
                rows={(filteredTestCase || []).slice(
                  (page - 1) * rowsPerPage,
                  page * rowsPerPage,
                )}
                columns={COLUMNS_ARTIFACT}
                showPagination={true}
                page={page}
                rowsPerPage={rowsPerPage}
                handlePageChange={setPage}
                totalItems={(filteredTestCase || []).length}
              />
            </Box>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <CustomButtonV2 btnName={"Cancel"} type="cancel" />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <CustomButtonV2
                btnName={"Save"}
                handleOnClick={() => {
                  setSavedRunTestCases((prev) => {
                    const updatedList = [...(prev ?? [])];
                    const existingIndex = updatedList.findIndex(
                      (item) => item?.test_case_id === selectedTestCase,
                    );

                    if (selectedRunTestCaseIds?.length === 0) {
                      existingIndex !== -1 &&
                        updatedList.splice(existingIndex, 1);
                    } else {
                      const testCaseData = {
                        test_case_id: selectedTestCase,
                        run_ids: [...selectedRunTestCaseIds],
                      };

                      existingIndex !== -1
                        ? (updatedList[existingIndex] = testCaseData)
                        : updatedList.push(testCaseData);
                    }
                    return updatedList;
                  });
                }}
                sx={{ mr: 2 }}
              />
              <CustomButtonV2
                btnName={"Review List"}
                disabled={savedRunTestCases?.length === 0}
                handleOnClick={() => {
                  setIsAddArtifactsScreenOpen(false);
                  handleGetReviewTestCaseArtifacts({ savedRunTestCases });
                }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          <Grid item sm={12}>
            <Box sx={{ height: "250px" }}>
              <CustomDataGridV2
                rows={(reviewTableRowsDataArtifact || []).slice(
                  (reviewPage - 1) * reviewRowsPerPage,
                  reviewPage * reviewRowsPerPage,
                )}
                columns={COLUMNS_ARTIFACT}
                showPagination={true}
                page={reviewPage}
                rowsPerPage={reviewRowsPerPage}
                handlePageChange={setReviewPage}
                totalItems={(reviewTableRowsDataArtifact || []).length}
              />
            </Box>
          </Grid>
          <Grid
            item
            sm={12}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box>
              <CustomButtonV2
                btnName={"Back"}
                handleOnClick={() => {
                  setIsAddArtifactsScreenOpen(true);
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <CustomButtonV2 btnName={"Cancel"} type="cancel" />
              <CustomButtonV2
                btnName={"Attach to Ticket"}
                handleOnClick={() => {
                  handleCreateTestCaseArtifacts({
                    intrId: selectedTicketsTableRowData?.intr_id,
                    selectedTestCaseArtifactIds: selectedRunTestCaseIds,
                    tableRowsDataArtifact: reviewTableRowsDataArtifact,
                  });
                }}
              />
            </Box>
          </Grid>
        </Box>
      )}

      <ViewImage
        open={openViewFilesDialog}
        onClose={() => setOpenViewFilesDialog(false)}
        dialogTitle="View Uploaded Files"
        selectedTestCaseData={selectedTestCaseArtifactData}
        setSelectedTestCaseData={setSelectedTestCaseArtifactData}
      />
    </Grid>
  );
}

export default AddTestCaseArtifact;
